import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CourtEventApi, { CourtEventDto } from '../../api/CourtEventApi';
import AttendingControl from '../AttendingControl/AttendingControl';
import { staffTicketUrl } from '../../utils/appearanceAlertUrls';
import { Link } from 'react-router-dom';

export interface UnsetAttendanceBannerProps {
  onUpdated?: () => void;
}

export const UnsetAttendanceBanner: React.FC<UnsetAttendanceBannerProps> = ({ onUpdated }) => {
  const [hearings, setHearings] = useState<CourtEventDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadUnsetHearings = async () => {
    setLoading(true);
    try {
      const now = Date.now();
      const inSevenDays = now + 7 * 86_400_000;
      const list = await CourtEventApi.listCourtEvents({
        CourtEvent_Start_From: now,
        CourtEvent_Start_To: inSevenDays,
      });

      // Filter to hearings where attendance is NOT_SET or NO_ONE
      const needsAttendance = list.filter(
        (event) =>
          event.CourtEvent_IsHearing !== false &&
          (!event.CourtEvent_Attending ||
            event.CourtEvent_Attending === 'NOT_SET' ||
            event.CourtEvent_Attending === 'NO_ONE'),
      );

      // Sort by start time ascending
      needsAttendance.sort((a, b) => Number(a.CourtEvent_Start) - Number(b.CourtEvent_Start));
      setHearings(needsAttendance);
    } catch (err) {
      console.error('Failed to load upcoming unset hearings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnsetHearings();
  }, []);

  const handleRowAttendingChanged = () => {
    loadUnsetHearings();
    onUpdated?.();
  };

  if (hearings.length === 0) {
    return null;
  }

  const unsetCount = hearings.filter(
    (h) => !h.CourtEvent_Attending || h.CourtEvent_Attending === 'NOT_SET',
  ).length;
  const noOneCount = hearings.filter((h) => h.CourtEvent_Attending === 'NO_ONE').length;

  const formatDate = (epoch: number) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(epoch));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="warning"
        icon={<WarningAmberIcon />}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={() => setExpanded(!expanded)}
            sx={{ fontWeight: 'bold' }}
          >
            {expanded ? 'Hide List' : `Clear Next 7 Days (${hearings.length})`}
          </Button>
        }
        sx={{
          bgcolor: '#fff8e1',
          border: '1px solid #ffe082',
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b78103' }}>
          Hearings needing attendance set: {hearings.length} in next 7 days
          {unsetCount > 0 && ` (${unsetCount} unassigned)`}
          {noOneCount > 0 && ` (${noOneCount} no one attending)`}
        </Typography>
        <Typography variant="body2" sx={{ color: '#5d4037', mt: 0.25 }}>
          Until staff confirm who is attending, Lloyd's 7:30 AM text will list unassigned hearings to be safe.
        </Typography>
      </Alert>

      <Collapse in={expanded}>
        <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#ffffff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              Upcoming Hearings Needing Attendance
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sorted by date — click Lloyd, Covering Attorney, or No One to assign
            </Typography>
          </Box>

          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 160 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Setting / Court</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Current Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 260 }}>Who's Attending</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">
                    Case
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hearings.map((event) => {
                  const isUnset =
                    !event.CourtEvent_Attending || event.CourtEvent_Attending === 'NOT_SET';
                  return (
                    <TableRow key={event.CourtEvent_guid} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {formatDate(Number(event.CourtEvent_Start))}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {event.CourtEvent_CalendarLabel || event.CourtEvent_Title || 'Hearing'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isUnset ? (
                          <Chip
                            size="small"
                            label="Not Set"
                            sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="No One"
                            sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <AttendingControl
                          courtEventGuid={event.CourtEvent_guid}
                          value={event.CourtEvent_Attending}
                          coveringAttorneyId={event.CourtEvent_CoveringAttorney_id}
                          coveringAttorneyGuid={event.CourtEvent_CoveringAttorney_guid}
                          coveringAttorneyName={event.CourtEvent_CoveringAttorney_Name}
                          setBy={event.CourtEvent_AttendingSetBy}
                          setAt={event.CourtEvent_AttendingSetAt}
                          isHearing={true}
                          compact={true}
                          autoSave={true}
                          onChanged={handleRowAttendingChanged}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {event.Ticket_guid && (
                          <Button
                            size="small"
                            component={Link}
                            to={staffTicketUrl(event.Ticket_guid, event.CourtEvent_guid)}
                          >
                            Open
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default UnsetAttendanceBanner;
